import { Box, Button, Card, Chip, CircularProgress, Typography } from '@mui/material'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import WTypography from '../../../../Components/whiteTypography'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfo } from '@fortawesome/free-solid-svg-icons/faInfo'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import QuestionAnswerFormTemps from './QuestionAnswerForm'
import { isEmpty } from 'lodash'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome' // LAP-765-CARD
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward' // LAP-765-CARD
import RestartAltIcon from '@mui/icons-material/RestartAlt' // LAP-765-CARD
import HistoryIcon from '@mui/icons-material/History' // LAP-765-CARD
import { useSelector, useDispatch } from 'react-redux'
import LockIcon from '@mui/icons-material/Lock'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)
import dayjs from 'dayjs' // LAP-765-CARD
import 'dayjs/locale/fr'
import relativeTime from 'dayjs/plugin/relativeTime'
import { makeGetRequest } from 'utils/api' // LAP-765-CARD
import urls from 'utils/urls' // LAP-765-CARD
import { loadWallet } from '../../../../Components/Billing/billingThunks' // LAP-765-CARD
import { buildTimeAdvisor } from '../../../ConseillerTemps/utils/buildTimeAdvisor' // LAP-765-CARD
import { fetchTimeAdvisorSources } from '../../../ConseillerTemps/utils/fetchTimeAdvisorSources' // LAP-765-CARD
import TimeRecommandationsHistoryModal from './TimeRecommandationsHistoryModal' // LAP-765-CARD

dayjs.extend(relativeTime)

// LAP-765-CARD — bandes de couleur du score (miroir TimeAdvisorSections.scoreColor).
function scoreBand(score) {
  if (score == null) return { color: '#94a3b8', bg: 'rgba(148,163,184,0.18)' }
  if (score >= 70) return { color: '#4ade80', bg: 'rgba(74,222,128,0.18)' }
  if (score >= 45) return { color: '#fbbf24', bg: 'rgba(251,191,36,0.18)' }
  return { color: '#f87171', bg: 'rgba(248,113,113,0.18)' }
}

/**
 * ConseilsCard — Dashboard "Synthèse Temps" (/syntheseTemps).
 *
 * LAP-765-CARD — Carte d'accès au conseiller temps IA, miroir de la carte
 * patrimoniale (MonPatrimoine/Component/Card/ConseilsCard) :
 *   - La card entière est cliquable et amène à /conseiller-temps (score +
 *     diagnostic + actions + time-blocks + chat).
 *   - Chip score : VRAI score temps calculé via buildTimeAdvisor (les mêmes
 *     sources que la page conseiller, agrégées par fetchTimeAdvisorSources).
 *   - "Nouvelle recommandation" : régénère le conseil du jour (1 crédit).
 *   - "Historique" : ouvre la liste des recommandations passées.
 */
function ConseilsCard({ oldData, updateReports }) {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  let w_tprofile = sessionStorage.getItem('w-tprofile')
  const [open, setOpen] = useState(false)
  const [regenerating, setRegenerating] = useState(false) // LAP-765-CARD
  const [historyOpen, setHistoryOpen] = useState(false) // LAP-765-CARD
  const [timeScore, setTimeScore] = useState(null) // LAP-765-CARD
  const [scoreLoading, setScoreLoading] = useState(false) // LAP-765-CARD
  const navigate = useNavigate()
  const dispatch = useDispatch() // LAP-765-CARD
  const siteUser = useSelector((store) => store.user)
  const isSubscribed = !!siteUser?.user?.has_subscription

  useEffect(() => {
    if (w_tprofile) {
      setOpen(true)
    }
  }, [])
  useEffect(() => {}, [oldData])

  // LAP-765-CARD — Calcul du VRAI score temps (une fois, au montage si
  // l'utilisateur est abonné). Le score est numérique et ne dépend pas de la
  // langue, donc on ne recalcule pas à chaque changement de `t`.
  useEffect(() => {
    if (!isSubscribed) return
    let cancelled = false
    const computeScore = async () => {
      setScoreLoading(true)
      try {
        const sources = await fetchTimeAdvisorSources(siteUser?.user)
        if (cancelled) return
        const advisor = buildTimeAdvisor({ ...sources, t })
        if (!cancelled) setTimeScore(advisor?.timeScore?.global ?? null)
      } catch (e) {
        if (!cancelled) setTimeScore(null)
      } finally {
        if (!cancelled) setScoreLoading(false)
      }
    }
    computeScore()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubscribed])

  // LAP-765-CARD — navigation vers la sous-page Conseiller Temps.
  const goToAdvisor = (e) => {
    if (e) e.stopPropagation()
    navigate('/conseiller-temps')
  }

  // LAP-765-CARD — régénération du conseil du jour (1 crédit côté backend).
  // On passe ?refresh=conseil (ignoré sans risque si non géré par le backend)
  // puis on rafraîchit les reports du dashboard + le solde de crédits.
  const handleNewReport = async (e) => {
    if (e) e.stopPropagation()
    setRegenerating(true)
    try {
      await makeGetRequest(`${urls.getSynthesTemps}?refresh=conseil`, {}, true)
      if (typeof updateReports === 'function') {
        await updateReports()
      }
      try {
        await dispatch(loadWallet())
      } catch (_) {
        // silencieux
      }
    } finally {
      setRegenerating(false)
    }
  }

  const openHistory = (e) => {
    if (e) e.stopPropagation()
    setHistoryOpen(true)
  }

  // LAP-733 (GE022) — override backend "Api OpenAi"/"OpenAI"/"ChatGPT" signature
  // with the unified "Life Agent" label.
  const displaySource = (() => {
    const src = oldData?.source
    if (!src) return src
    if (/(open\s*ai|openai|chat\s*gpt|chatgpt|gpt-?\d)/i.test(String(src))) {
      return t('syntheseTemps.lifeAgentSignature')
    }
    return src
  })()

  // LAP-765-CARD — date du dernier rapport (champ variable selon backend).
  const reportDate =
    oldData?.created_at ||
    oldData?.createdAt ||
    oldData?.generated_at ||
    oldData?.date ||
    oldData?.timestamp
  const relativeDate = (() => {
    if (!reportDate) return ''
    try {
      return dayjs(reportDate).fromNow()
    } catch {
      return ''
    }
  })()

  const band = scoreBand(timeScore)

  // ── Branche NON ABONNÉ : lock (inchangé) ──────────────────────────────
  if (!isSubscribed) {
    return (
      <Card className="dashboard_card__root1">
        <Box
          sx={{
            padding: 4,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="h5"
            component="h1"
            sx={{ color: '#e0e0e0', fontWeight: 600, marginBottom: 4 }}
          >
            {t('syntheseTemps.adviceTitle')}
          </Typography>

          <LockIcon sx={{ fontSize: 80, color: '#5a5a6b', marginBottom: 4 }} />

          <Typography
            variant="h6"
            sx={{ color: '#e0e0e0', marginBottom: 6, lineHeight: 1.5 }}
          >
            {t('syntheseTemps.adviceLockedSubtitle')}
          </Typography>

          <Button
            variant="contained"
            className="no-drag"
            sx={{
              width: '100%',
              backgroundColor: '#38384d',
              color: '#e0e0e0',
              padding: '12px 0',
              fontSize: '1rem',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#4a4a5f' },
            }}
            onClick={() => {
              navigate('/subscription')
            }}
          >
            {t('syntheseTemps.goToPremium')}
          </Button>
        </Box>
      </Card>
    )
  }

  // ── Branche ABONNÉ : carte conseiller (miroir finance) ────────────────
  return (
    <>
      <Card
        className="dashboard_card__root1 no-drag"
        onClick={goToAdvisor}
        sx={{
          cursor: 'pointer',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          transition:
            'transform .25s ease, box-shadow .25s ease, border-color .25s ease',
          border: '1px solid rgba(155,140,255,0.20)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(155,140,255,0.25)',
            borderColor: 'rgba(155,140,255,0.55)',
          },
          // Halo décoratif violet AI
          '&::after': {
            content: '""',
            position: 'absolute',
            top: -60,
            right: -60,
            width: 180,
            height: 180,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(155,140,255,0.22) 0%, transparent 70%)',
            pointerEvents: 'none',
          },
        }}
      >
        {/* Header : icône AI + titre + chip score réel */}
        <Box
          className="dashboard_card__header1"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesomeIcon sx={{ color: '#9b8cff', fontSize: 22 }} />
            <WTypography component={'span'} variant="h6">
              {t('syntheseTemps.adviceCardTitle')}
            </WTypography>
          </Box>
          {scoreLoading ? (
            <Chip
              size="small"
              icon={<CircularProgress size={12} sx={{ color: '#a0b4d6' }} />}
              label={t('syntheseTemps.scoreLoading', { defaultValue: 'Score…' })}
              sx={{
                bgcolor: 'rgba(148,163,184,0.18)',
                color: '#cbd5e1',
                fontWeight: 700,
                height: 22,
              }}
            />
          ) : (
            timeScore != null && (
              <Chip
                label={t('syntheseTemps.scoreLabel', {
                  score: timeScore,
                  defaultValue: 'Score {{score}}/100',
                })}
                size="small"
                sx={{ bgcolor: band.bg, color: band.color, fontWeight: 700, height: 22 }}
              />
            )
          )}
        </Box>

        {/* Sous-header : "Conseil du jour · Généré il y a X" */}
        <Box
          className="dashboard_card__header1"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <WTypography sx={{ color: '#ffffffa1' }} variant={'body2'}>
            {t('syntheseTemps.adviceCardSubtitle')}
          </WTypography>
          {relativeDate && (
            <WTypography sx={{ color: '#ffffffa1' }} variant={'body2'}>
              ·{' '}
              {t('syntheseTemps.generatedRelative', {
                when: relativeDate,
                defaultValue: 'Généré {{when}}',
              })}
            </WTypography>
          )}
        </Box>

        {/* Zone conseil */}
        <Box
          mb={2}
          sx={{
            position: 'relative',
            zIndex: 1,
            flex: 1,
            minHeight: 0,
            px: 1,
            mt: { xs: 2, sm: 4 },
            overflowY: 'auto',
            overflowX: 'hidden',
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(155,140,255,0.35)',
              borderRadius: '3px',
            },
          }}
        >
          {isEmpty(oldData) ? (
            // État vide : on garde le questionnaire de profil temps (capture du
            // type de gestionnaire de temps), qui alimente les recommandations.
            <Box
              className="no-drag"
              justifyContent={'center'}
              color={'white'}
              display={'flex'}
              flexDirection={'column'}
              alignItems={'center'}
              onClick={(e) => e.stopPropagation()}
            >
              <QuestionAnswerFormTemps
                open={open}
                setOpen={setOpen}
                startIcon={
                  <Box
                    color={'#00295f'}
                    padding={4}
                    bgcolor={'white'}
                    borderRadius={'50%'}
                    mb={3}
                  >
                    <QuestionMarkIcon sx={{ fontSize: '48px' }} />
                  </Box>
                }
                updateReports={updateReports}
              />
              <Box padding={'0 20px'} textAlign={'center'}>
                {t('syntheseTemps.timeProfileQuestionPrompt')}
              </Box>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  borderLeft: '3px solid #9b8cff',
                  pl: 2.5,
                  pr: 1,
                  py: 1,
                  color: 'white',
                  fontStyle: 'italic',
                  lineHeight: 1.7,
                  fontSize: 16,
                }}
              >
                <Box>{oldData?.content}</Box>
                <Box
                  mt={1.5}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 0.75,
                    color: '#9b8cff',
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: 0.3,
                    fontStyle: 'normal',
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                  {displaySource /* LAP-733 (GE022) */}
                </Box>
              </Box>
              {oldData?.function && (
                <Box color={'white'} textAlign={'center'} mt={2}>
                  {oldData?.function}
                  <Box
                    className="no-drag"
                    sx={{ cursor: 'pointer' }}
                    component={'span'}
                    ml={2}
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(oldData.function_link)
                    }}
                  >
                    <KeyboardArrowRightIcon
                      sx={{ bgcolor: '#438295', borderRadius: '50%' }}
                    />
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Boutons d'action : Nouvelle recommandation + Historique */}
        {!isEmpty(oldData) && (
          <Box
            className="no-drag"
            sx={{
              display: 'flex',
              gap: 1,
              mx: 2,
              mb: 1.5,
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Button
              fullWidth
              onClick={handleNewReport}
              disabled={regenerating}
              startIcon={
                <RestartAltIcon
                  sx={{
                    animation: regenerating ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
              }
              sx={{
                bgcolor: 'rgba(155,140,255,0.18)',
                color: '#d4ccff',
                fontWeight: 600,
                textTransform: 'none',
                border: '1px solid rgba(155,140,255,0.4)',
                py: 0.75,
                '&:hover': {
                  bgcolor: 'rgba(155,140,255,0.30)',
                  borderColor: '#9b8cff',
                },
                '&.Mui-disabled': {
                  color: 'rgba(212,204,255,0.5)',
                  borderColor: 'rgba(155,140,255,0.2)',
                },
              }}
            >
              {regenerating
                ? t('syntheseTemps.regenerating', { defaultValue: 'Génération…' })
                : t('syntheseTemps.newReport', {
                    defaultValue: 'Nouvelle recommandation',
                  })}
            </Button>
            <Button
              fullWidth
              onClick={openHistory}
              startIcon={<HistoryIcon />}
              sx={{
                color: '#dbe5f7',
                fontWeight: 600,
                textTransform: 'none',
                border: '1px solid rgba(255,255,255,0.18)',
                py: 0.75,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.06)',
                  borderColor: 'rgba(255,255,255,0.3)',
                },
              }}
            >
              {t('syntheseTemps.viewHistory', { defaultValue: 'Historique' })}
            </Button>
          </Box>
        )}

        {/* CTA principal vers la sous-page Conseiller Temps */}
        <Box
          className="no-drag"
          onClick={goToAdvisor}
          sx={{
            mx: 2,
            mb: 2,
            py: 1,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            bgcolor: 'rgba(59,130,246,0.18)',
            border: '1px solid rgba(59,130,246,0.4)',
            borderRadius: 1.5,
            cursor: 'pointer',
            transition: 'all .2s',
            position: 'relative',
            zIndex: 1,
            '&:hover': {
              bgcolor: 'rgba(59,130,246,0.30)',
              borderColor: '#3b82f6',
            },
          }}
        >
          <WTypography sx={{ color: '#a8c1f0', fontWeight: 600 }} variant="body2">
            {t('syntheseTemps.voirAnalyseTempsComplete', {
              defaultValue: 'Voir mon analyse temps complète',
            })}
          </WTypography>
          <ArrowOutwardIcon sx={{ color: '#a8c1f0', fontSize: 18 }} />
        </Box>

        {/* Accès discret au questionnaire profil temps même quand oldData présent */}
        {!isEmpty(oldData) && (
          <Box
            mt={1}
            className="dashboard_card__filters1 no-drag"
            onClick={(e) => e.stopPropagation()}
          >
            <Box>
              <QuestionAnswerFormTemps
                open={open}
                setOpen={setOpen}
                startIcon={
                  <Box
                    color={'#00295f'}
                    padding={'0 8px'}
                    bgcolor={'white'}
                    borderRadius={'50%'}
                    mb={1}
                    mr={2}
                  >
                    <FontAwesomeIcon icon={faInfo} />
                  </Box>
                }
                updateReports={updateReports}
              />
            </Box>
          </Box>
        )}
      </Card>

      {/* Modal historique (hors card pour éviter la navigation parent) */}
      <TimeRecommandationsHistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        currentReport={oldData}
      />
    </>
  )
}

export default ConseilsCard
